import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Search, Users, Signal, Play } from "lucide-react";
import AppLayout from "../components/AppLayout";
import api from "../services/api";

const DEFAULT_ART = "/microphone.png";
const NP_POLL_MS = 20000;

export default function PublicRadios() {
  const { t } = useTranslation();
  const [radios, setRadios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [genre, setGenre] = useState("");
  const [live, setLive] = useState({}); // { [radioId]: { online, listeners, text } }

  useEffect(() => {
    let mounted = true;
    api.get("/api/radios/public")
      .then((res) => { if (mounted) setRadios(Array.isArray(res.data) ? res.data : []); })
      .catch(() => { if (mounted) setRadios([]); })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, []);

  // Best-effort live now-playing per radio (listener count + current song).
  useEffect(() => {
    if (radios.length === 0) return undefined;
    let mounted = true;
    const loadAll = () => {
      radios.forEach((r) => {
        api.get(`/api/radios/${r.id}/nowplaying`)
          .then((res) => {
            if (!mounted) return;
            const np = res.data || {};
            const song = np?.now_playing?.song || {};
            setLive((prev) => ({
              ...prev,
              [r.id]: {
                online: !!np.is_online,
                listeners: np?.listeners?.current ?? 0,
                text: song.text || [song.artist, song.title].filter(Boolean).join(" — "),
              },
            }));
          })
          .catch(() => {});
      });
    };
    loadAll();
    const poll = setInterval(loadAll, NP_POLL_MS);
    return () => { mounted = false; clearInterval(poll); };
  }, [radios]);

  const genres = useMemo(() => {
    const set = new Set();
    radios.forEach((r) => { if (r.genre) set.add(r.genre); });
    return Array.from(set).sort();
  }, [radios]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return radios.filter((r) => {
      if (genre && r.genre !== genre) return false;
      if (!q) return true;
      return `${r.name || ""} ${r.genre || ""} ${r.language || ""} ${r.owner_firstname || ""}`
        .toLowerCase().includes(q);
    });
  }, [radios, query, genre]);

  return (
    <AppLayout title={t("publicRadios.title")}>
      {/* Filters */}
      <div className="flex items-center gap-3 mb-6 flex-wrap">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 text-gray-400 absolute top-1/2 -translate-y-1/2 start-3" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t("publicRadios.searchPlaceholder")}
            className="w-full rounded-xl border border-gray-200 bg-white ps-9 pe-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-purple-200"
          />
        </div>
        {genres.length > 0 && (
          <select
            value={genre}
            onChange={(e) => setGenre(e.target.value)}
            className="rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-200"
          >
            <option value="">{t("publicRadios.allGenres")}</option>
            {genres.map((g) => <option key={g} value={g}>{g}</option>)}
          </select>
        )}
      </div>

      {loading ? (
        <p className="text-gray-500">{t("common.loading")}</p>
      ) : radios.length === 0 ? (
        <p className="text-gray-500">{t("publicRadios.empty")}</p>
      ) : filtered.length === 0 ? (
        <p className="text-gray-500">{t("publicRadios.noResults")}</p>
      ) : (
        <div className="grid gap-3 md:grid-cols-2">
          {filtered.map((r) => {
            const info = live[r.id];
            return (
              <Link
                key={r.id}
                to={`/r/${r.slug}`}
                className="group bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-md hover:border-purple-200 transition p-4 flex items-center gap-3"
              >
                {r.logo ? (
                  <img src={r.logo} alt="" className="w-14 h-14 rounded-xl object-cover bg-gray-100 shrink-0" onError={(e) => { e.currentTarget.src = DEFAULT_ART; }} />
                ) : (
                  <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-purple-500 to-blue-500 text-white flex items-center justify-center text-2xl shrink-0">📻</div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-gray-900 truncate group-hover:text-purple-700">{r.name}</span>
                    {info?.online && (
                      <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded-full shrink-0">
                        <Signal className="w-3 h-3" /> {t("publicRadio.live")}
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-gray-500 truncate">
                    {[r.genre, r.language].filter(Boolean).join(" • ")}
                    {r.owner_firstname ? ` • ${t("publicRadio.by", { name: r.owner_firstname })}` : ""}
                  </div>
                  <div className="text-xs text-gray-400 truncate mt-0.5">
                    {info?.text
                      ? info.text
                      : (
                        <span className="inline-flex items-center gap-1">
                          <Users className="w-3 h-3" />
                          {t("publicRadio.listeners", { count: info?.listeners ?? 0 })}
                        </span>
                      )}
                  </div>
                </div>
                <span className="w-10 h-10 rounded-full bg-purple-600 group-hover:bg-purple-700 text-white flex items-center justify-center shrink-0">
                  <Play className="w-4 h-4" />
                </span>
              </Link>
            );
          })}
        </div>
      )}
    </AppLayout>
  );
}
